import React, { useState, useEffect } from 'react';
import { Icon } from '../../components/Icon';
import { Table } from './Table';
import { useLead } from '../../hooks/useLeads';
import { LeadFormCard } from '../../components/LeadFormCard';
import { LeadsSearchFilters, type LeadFilters } from '../../components/LeadsSearchFilters';
import { usePersistedState } from '../../hooks/usePersistedState';
import { ExportModal } from '../../components/ExportModal'; 

const Leads: React.FC = () => {

    const [pageSize, setPageSize] = useState(10);
    const [personType] = useState("");
    const [showLeadForm, setShowLeadForm] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false); 
    const [currentPage, setCurrentPage] = usePersistedState<number>('leads_page', 1);
    const [filters, setFilters] = usePersistedState<LeadFilters>('leads_filters', {
        searchTerm: '',
        agentId: '',
        sourceId: '',
        statusId: ''
    });

    const handleOpenForm = () => {
        setShowLeadForm(prev => !prev);
    };

    const handleCloseForm = () => {
        setShowLeadForm(false);
    };

    const {
        data: leads,
        users,
        statusCount,
        loading,
        error,
        total,
        totalPages,
        refetch,
        creating,
        createLead,
        bulkAssignLeads
    } = useLead({
        tipo_persona_id: personType,
        page: currentPage,
        pageSize,
        searchTerm: filters.searchTerm,
        agentId: filters.agentId,
        sourceId: filters.sourceId,
        statusId: filters.statusId
    });

    // Reset page when search, filters or pageSize change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.searchTerm, filters.agentId, filters.sourceId, filters.statusId, pageSize]);

    const handleFiltersChange = (newFilters: LeadFilters) => {
        setFilters(newFilters);
    };

    const handleSearch = () => {
        refetch();
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
    };

    if (error) {
        return (
            <div className="space-y-6 animate-fade-in">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Gestión de Leads
                    </h2>
                </div>
                <div className="card p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <div className="flex items-center">
                        <Icon name="alert-circle" className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-700 dark:text-red-300">
                            Error: {error}
                        </span>
                    </div>
                    <button
                        onClick={refetch}
                        className="mt-3 btn-primary"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Gestión de Leads
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {total} leads encontrados
                    </p>
                </div>
                <div className="flex items-center space-x-3">

                    {/* ── Botón Exportar Excel ── */}
                    <button
                        onClick={() => setShowExportModal(true)}
                        disabled={loading || leads.length === 0}
                        className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                        title="Descargar lista como Excel"
                    >
                        <Icon name="download" className="w-4 h-4 mr-2" />
                        Exportar Excel
                    </button>

                    {/* ── Botón Nuevo Lead ── */}
                    <button
                        onClick={handleOpenForm}
                        className={`flex items-center px-4 py-2 text-sm font-medium text-white ${showLeadForm ? "bg-red-400 hover:bg-red-500" : "bg-blue-600 hover:bg-blue-700"} rounded-lg transition-colors`}
                    >
                        {showLeadForm ? (
                            <>
                                <Icon name="close" className="w-4 h-4 mr-2" />
                                Cancelar
                            </>
                        ) : (
                            <>
                                <Icon name="plus" className="w-4 h-4 mr-2" />
                                Nuevo Lead
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Status counts */}
            <div className="dark:bg-gray-800 rounded-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                    {statusCount.map((item, index) => (
                        item.lead_count > 0 && (
                            <div
                                key={index}
                                className="flex items-center space-x-2 bg-white dark:bg-gray-700 rounded-md border border-primary p-3"
                            >
                                <span className="text-xl font-bold text-primary dark:text-blue-400">
                                    {item.lead_count}
                                </span>
                                <span className="text-gray-700 dark:text-gray-300">{item.status_name}</span>
                            </div>
                        )
                    ))}
                </div>
            </div>

            {/* Lead form */}
            <LeadFormCard
                creating={creating}
                isVisible={showLeadForm}
                onClose={handleCloseForm}
                onSubmit={createLead}
            />

            {/* Filtros */}
            <LeadsSearchFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onSearch={handleSearch}
                users={users}
            />

            {/* Page size */}
            <div className="flex items-center space-x-2">
                <label htmlFor="pageSize" className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrar:
                </label>
                <select
                    id="pageSize"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
            </div>

            {/* Table */}
            <Table
                data={leads}
                users={users}
                loading={loading}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                total={total}
                onPageChange={setCurrentPage}
                onRefresh={refetch}
                bulkAssignLeads={bulkAssignLeads}
            />

            {/* ── Modal de exportación ── */}
            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                baseFilters={filters}
                total={total}
                users={users}
            />
        </div>
    );
};

export default Leads;